import L from 'leaflet';

// --- Calculation Logic Ported from locatia-state.js ---

function movePosition(lat: number, lng: number, distance: number, bearingDegrees: number) {
    const R = 6371000; // Radius of Earth in meters
    const δ = distance / R; // Angular distance in radians
    const θ = bearingDegrees * Math.PI / 180; // Bearing in radians
    const φ1 = lat * Math.PI / 180; // Latitude in radians
    const λ1 = lng * Math.PI / 180; // Longitude in radians

    const φ2 = Math.asin(Math.sin(φ1) * Math.cos(δ) + Math.cos(φ1) * Math.sin(δ) * Math.cos(θ));
    const λ2 = λ1 + Math.atan2(Math.sin(θ) * Math.sin(δ) * Math.cos(φ1), Math.cos(δ) - Math.sin(φ1) * Math.sin(φ2));

    return {
        lat: φ2 * 180 / Math.PI, // Convert back to degrees
        lng: λ2 * 180 / Math.PI  // Convert back to degrees
    };
}

function simulateTrackOverGround(lat: number, lng: number, speed: number, bearing: number, duration: number, legnum: number, driftRate: number, driftBrg: number) {
    const result = [];
    if (legnum === 1) { // Original code had this condition, but legnum starts at 0 for first leg
        result.push({y: lat.toFixed(5), x: lng.toFixed(5), l: 0}); // Start point is leg 0
    }
    const newPosition = movePosition(lat, lng, speed * duration, bearing);
    const driftDistance = driftRate * duration;
    const newPositionWithDrift = movePosition(newPosition.lat, newPosition.lng, driftDistance, driftBrg);
    result.push({y: newPositionWithDrift.lat, x: newPositionWithDrift.lng, l: legnum});
    return result;
}

function simulateTrackOverSurface(lat: number, lng: number, speed: number, bearing: number, duration: number, legnum: number) {
    const result = [];
    if (legnum === 1) { // Original code had this condition, but legnum starts at 0 for first leg
        result.push({y: lat.toFixed(5), x: lng.toFixed(5), l: legnum});
    }
    const newPosition = movePosition(lat, lng, speed * duration, bearing);
    result.push({y: newPosition.lat, x: newPosition.lng, l: legnum});
    return result;
}

// This is the CORRECT version ported from the user-provided locatia-state.js
function calculateEachLegTableData(legnum: number, heading: number, elapsedTime: number, pattern: string, sdpd: number, crossLegAdjust: number, crossLegLength: number, creepFirstLegLength: string, spdk: number) {
    const searchDataInputSpdk = spdk; // Use passed spdk
    const searchDirectionProgressDefault = sdpd; // Use passed sdpd

    let duration = 0;
    let distance = 0;
    let newHeading = heading;

    if (pattern === 'sector') {
        newHeading += legnum % 3 === 0 ? 0 : 120;
        if ((legnum > 0) && (legnum % 9 === 0)) { // This condition seems specific to a larger pattern cycle
            newHeading = newHeading + 30;
        }
        distance = searchDirectionProgressDefault;
        duration = sdpd / (searchDataInputSpdk / 2);
    } else if (pattern === 'box') {
        distance = (Math.trunc(legnum / 2) * searchDirectionProgressDefault) + searchDirectionProgressDefault;
        duration = distance / (searchDataInputSpdk / 2);
        newHeading += legnum === 0 ? 0 : 90; // Add 90 degrees for box pattern
    } else if (pattern === 'creep') {
        if (legnum % 4 === 0) {
            newHeading = heading + 90 + crossLegAdjust;
            // Apply creepFirstLegLength: default behaviour is 'half'
            distance = (legnum === 0 && creepFirstLegLength === 'half') ? sdpd / 2 : sdpd;
        } else if (legnum % 4 === 1) {
            newHeading = heading - 90 + crossLegAdjust;
            distance = crossLegLength;
        } else if (legnum % 4 === 2) {
            newHeading = heading - 90 + crossLegAdjust;
            distance = sdpd;
        } else if (legnum % 4 === 3) {
            newHeading = heading + 90 + crossLegAdjust;
            distance = crossLegLength;
        }
        duration = distance / (searchDataInputSpdk / 2);
    }
    newHeading = (newHeading + 360) % 360;
    duration = Math.floor(duration);
    const endTime = elapsedTime + duration;
    return {
        legnum: legnum + 1, // Leg numbers start at 1
        bearing: newHeading, duration, distance: distance, startTime: elapsedTime, endTime,
    };
}


function generateLegs(inputData: any) {
    const legs = [];
    let currentHeading = parseInt(inputData.hdg, 10);
    let elapsedTime = 0;
    const crossLegAdjust = inputData.crossLegTurn === 'left' ? 0 : 180;
    const spdk = parseFloat(inputData.spdk);
    const searchLegLength = parseInt(inputData.searchLegLength, 10);
    const crossLegLength = parseInt(inputData.crossLegLength, 10);


    for (let i = 0; i < parseInt(inputData.maxLegs, 10); i++) {
        const creepFirstLegLength = inputData.creepFirstLegLength || 'half';
        const leg = calculateEachLegTableData(i, currentHeading, elapsedTime, inputData.searchType, searchLegLength, crossLegAdjust, crossLegLength, creepFirstLegLength, spdk);
        elapsedTime += leg.duration;
        currentHeading = leg.bearing;
        legs.push(leg);
    }
    return legs;
}

function generateTrackOverGroundData(inputData: any, legs: any[]) {
    const lat = parseFloat(inputData.latDec);
    const lng = parseFloat(inputData.lngDec);
    const speed = parseFloat(inputData.spdk) / 2;
    const magVar = parseFloat(inputData.magVar) || 0;
    const driftRate = (parseFloat(inputData.driftDist) / parseFloat(inputData.driftTime)) || 0;
    const driftBrg = parseFloat(inputData.driftBrg);

    if (isNaN(lat) || isNaN(lng) || isNaN(speed)) return [];

    let tracks: any[] = [];
    let track = [{ "y": lat, "x": lng, "l": 0 }]; // Initial point for track

    legs.forEach((leg) => {
        const newTrackPoints = simulateTrackOverGround(track[track.length - 1].y, track[track.length - 1].x, speed, leg.bearing + magVar, leg.duration, leg.legnum, driftRate, driftBrg);
        track = newTrackPoints; // Update current track end point
        tracks = tracks.concat(newTrackPoints); // Add new points to overall tracks
    });
    return tracks;
}

function generateTrackOverSurfaceData(inputData: any, legs: any[]) {
    const lat = parseFloat(inputData.latDec);
    const lng = parseFloat(inputData.lngDec);
    const speed = parseFloat(inputData.spdk) / 2;
    const magVar = parseFloat(inputData.magVar) || 0;
    const driftDistInput = parseFloat(inputData.driftDist);
    const driftTimeInput = parseFloat(inputData.driftTime);
    let driftRate = 0;
    if (driftDistInput && driftTimeInput && driftTimeInput > 0) {
        driftRate = (driftDistInput / driftTimeInput);
    }

    if (isNaN(lat) || isNaN(lng) || isNaN(speed)) return [];
    
    let tracks: any[] = [];
    const driftDistance = legs.length > 0 ? legs[legs.length - 1].endTime * driftRate : 0;
    const newPositionWithDrift = movePosition(lat, lng, driftDistance, parseFloat(inputData.driftBrg));
    
    let track = [{
        "y": newPositionWithDrift.lat, "x": newPositionWithDrift.lng
    }];

    legs.forEach((leg) => {
        const newTrackPoints = simulateTrackOverSurface(track[track.length - 1].y, track[track.length - 1].x, speed, leg.bearing + magVar, leg.duration, leg.legnum);
        track = newTrackPoints;
        tracks = tracks.concat(track);
    });
    return tracks;
}


// --- Visualization Functions ---

// Store references to active layers so we can clear them
let activeSearchLayers: L.Layer[] = [];

export function clearSearchPatterns(map: L.Map) {
    activeSearchLayers.forEach(layer => {
        map.removeLayer(layer);
    });
    activeSearchLayers = [];
    map.getContainer().dispatchEvent(new CustomEvent('search-pattern-updated'));
}

export function visualizeSearchPattern(map: L.Map, searchPattern: Record<string, string>) {
    clearSearchPatterns(map); // Clear existing patterns first

    const legs = generateLegs(searchPattern);
    const groundPoints = generateTrackOverGroundData(searchPattern, legs);
    const surfacePoints = generateTrackOverSurfaceData(searchPattern, legs);
    
    if (groundPoints && groundPoints.length > 0) {
        addPspOverGroundToMap(map, groundPoints, searchPattern);
    }
    if (surfacePoints && surfacePoints.length > 0) {
        addPspOverSurfaceToMap(map, surfacePoints, searchPattern);
    }
    addDatumToMap(map, searchPattern);
    
    map.getContainer().dispatchEvent(new CustomEvent('search-pattern-updated'));
}

export function addPspOverGroundToMap(map: L.Map, points: any[], inputdata: any) {
    const path: L.LatLngExpression[] = [];
    points.forEach((point: any) => {
        const lat = parseFloat(point.y);
        const lng = parseFloat(point.x);
        path.push([lat, lng]);

        if (point.l >= 0) { 
            const marker = L.marker([lat, lng], {
                icon: L.divIcon({
                    className: 'custom-circle-marker',
                    html: `<div class="circle-with-text">${point.l}</div>`,
                    iconSize: [20, 20],
                    iconAnchor: [10, 10]
                })
            }).addTo(map);
            (marker as any)._locatiaType = 'ground-track';
            activeSearchLayers.push(marker);
        }
    });

    const polyline = L.polyline(path, { color: '#000000', weight: 2, opacity: 0.8, dashArray: '4, 6' }).addTo(map);
    (polyline as any)._locatiaType = 'ground-track';
    activeSearchLayers.push(polyline);

    const bsd = parseFloat(inputdata.bsd);
    if (!isNaN(bsd)) {
        // Calculate weight once at creation
        const weight = Math.max(metersToPixels(map, bsd) * 2, 1); // Ensure at least 1px
        const bsdLine = L.polyline(path, { color: '#0000ff', weight: weight, opacity: 0.3 }).addTo(map);
        (bsdLine as any)._locatiaType = 'ground-bsd';
        activeSearchLayers.push(bsdLine);
        
        // Add zoomend listener to update weight efficiently
        const updateWeight = () => {
            bsdLine.setStyle({ weight: Math.max(metersToPixels(map, bsd) * 2, 1) });
        };
        map.on('zoomend', updateWeight);
        
        // Ensure listener is removed when layer is removed
        bsdLine.on('remove', () => {
            map.off('zoomend', updateWeight);
        });
    }
}

export function addPspOverSurfaceToMap(map: L.Map, points: any[], inputdata: any) {
    if (!points || points.length === 0) return;
    const path: L.LatLngExpression[] = [];
    points.forEach((point: any) => {
        const lat = parseFloat(point.y);
        const lng = parseFloat(point.x);
        path.push([lat, lng]);
    });
    
    const bsd = parseFloat(inputdata.bsd);
    if (!isNaN(bsd)) {
        // Calculate weight once at creation
        const weight = Math.max(metersToPixels(map, bsd) * 2, 1); // Ensure at least 1px
        const bsdLine = L.polyline(path, { color: '#800000', weight: weight, opacity: 0.4 }).addTo(map);
        (bsdLine as any)._locatiaType = 'surface-bsd';
        activeSearchLayers.push(bsdLine);
        
        // Add zoomend listener to update weight efficiently
        const updateWeight = () => {
            bsdLine.setStyle({ weight: Math.max(metersToPixels(map, bsd) * 2, 1) });
        };
        map.on('zoomend', updateWeight);
        
        // Ensure listener is removed when layer is removed
        bsdLine.on('remove', () => {
            map.off('zoomend', updateWeight);
        });
    }
}

export function addDatumToMap(map: L.Map, inputdata: any) {
    const latDecIn = parseFloat(inputdata.latDecIn);
    const lngDecIn = parseFloat(inputdata.lngDecIn);
    const latDecOut = parseFloat(inputdata.latDecOut);
    const lngDecOut = parseFloat(inputdata.lngDecOut);

    if (!isNaN(latDecIn) && !isNaN(lngDecIn) && !isNaN(latDecOut) && !isNaN(lngDecOut)) {
        const datumPath: L.LatLngExpression[] = [[latDecIn, lngDecIn], [latDecOut, lngDecOut]];
        const polyline = L.polyline(datumPath, { color: '#ff0000', weight: 2, opacity: 0.8, dashArray: '4, 6' }).addTo(map);
        (polyline as any)._locatiaType = 'ground-track';
        activeSearchLayers.push(polyline);

        const m1 = L.marker([latDecIn, lngDecIn], {
            icon: L.divIcon({
                className: 'custom-circle-marker',
                html: `<div class="circle-with-text">in</div>`,
                iconSize: [20, 20],
                iconAnchor: [10, 10]
            })
        }).addTo(map);
        (m1 as any)._locatiaType = 'ground-track';
        activeSearchLayers.push(m1);

        const m2 = L.marker([latDecOut, lngDecOut], {
            icon: L.divIcon({
                className: 'custom-circle-marker',
                html: `<div class="circle-with-text">out</div>`,
                iconSize: [20, 20],
                iconAnchor: [10, 10]
            })
        }).addTo(map);
        (m2 as any)._locatiaType = 'ground-track';
        activeSearchLayers.push(m2);
    }
}

function metersToPixels(map: L.Map, meters: number) {
    if (isNaN(meters)) return 0;
    const lat = map.getCenter().lat;
    const zoom = map.getZoom();
    const earthCircumference = 40075017;
    return meters / (earthCircumference * Math.cos(lat * Math.PI / 180) / Math.pow(2, zoom) / 256);
}

export function setSearchPatternLayerOpacity(type: string, opacity: number) {
    activeSearchLayers.forEach(layer => {
        if ((layer as any)._locatiaType === type) {
            if (layer instanceof L.Marker) {
                layer.setOpacity(opacity);
            } else {
                (layer as L.Polyline).setStyle({ opacity: opacity });
            }
        }
    });
}

export function getSearchPatternLayerOpacity(type: string): number | null {
    for (const layer of activeSearchLayers) {
        if ((layer as any)._locatiaType === type) {
            return (layer as L.Polyline).options.opacity || 1;
        }
    }
    return null;
}

export function hasSearchPatternLayers(): boolean {
    return activeSearchLayers.some(layer => (layer as any)._locatiaType);
}
